import dayjs from '~/utils/dayjsConfig';
import { useEffect, useState } from 'react';

import { productionApi } from '~/api/production';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shadcn/components/ui/Card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '~/shadcn/components/ui/Select';
import { cn } from '~/shadcn/lib/utils';
import { getNextWeekWednesday, getWeekNumber, getWeekYear } from '~/utils/dateUtils/dateUtils';

import DateWeekInput from '../../components/DateWeekInput';
import PackGroupInput from '../../components/PackGroupInput';

import { useDataLoader } from '../providers/DataLoaderProvider';
import { useFormControls } from '../providers/FormStateProvider';

const SEGMENT_VALUES = ['NEW_USERS', 'PLUS_MEMBERS'];

function MetadataCard({ formData }) {
    const { packGroupDate } = useDataLoader();
    const { highlightedSliceId, sliceCardRefs, updateConfig } = useFormControls();

    const [date, setDate] = useState();
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);

    const [packGroups, setPackGroups] = useState();

    useEffect(() => {
        if (packGroupDate) {
            setDate(packGroupDate);
        }
    }, [packGroupDate]);

    useEffect(() => {
        if (!date) return;

        const fetchPackGroups = async () => {
            try {
                const response = await productionApi.getPackGroups({
                    week: getWeekNumber(date, 'YYYY-MM-DD'),
                    year: getWeekYear(date, 'YYYY-MM-DD'),
                });

                // Filter out non-standard pack groups (ex. "LS CHI1", "ST CHI1", "SM CHI1")
                const filteredPackGroups = response.data.packGroups.filter(
                    (packGroup) => packGroup.name.split(' ').length === 1,
                );

                setPackGroups(filteredPackGroups);
            } catch (error) {
                console.error('Error fetching pack groups:', error);
            }
        };
        fetchPackGroups();
    }, [date]);

    useEffect(() => {
        const validPackGroupID = packGroups?.find((packGroup) => packGroup.id === formData.packGroupID);

        if (packGroups && !validPackGroupID) {
            updateConfig({ packGroupID: packGroups[0].id });
        }
    }, [packGroups]);

    const handleSelectDate = (date) => {
        setDate(date);
        setDatePopoverOpen(false);
    };

    const getDisabledPackGroupDates = (date) => dayjs(date).isSameOrAfter(getNextWeekWednesday(), 'day');

    return (
        <Card
            className={cn(highlightedSliceId === 'metadata' && 'outline outline-ring shadow-md')}
            ref={(el) => (sliceCardRefs.current['metadata'] = el)}
        >
            <CardHeader>
                <CardTitle>Metadata</CardTitle>
                <CardDescription>Data for the configuration unrelated to slices.</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2 my-2">
                            <div className="flex grow shrink-0 basis-[calc(50%-4px)]">
                                <DateWeekInput
                                    date={date}
                                    disabledDates={getDisabledPackGroupDates}
                                    onSelectDate={handleSelectDate}
                                    popoverOpen={datePopoverOpen}
                                    setPopoverOpen={setDatePopoverOpen}
                                />
                            </div>
                            <div className="flex grow shrink-0 basis-[calc(50%-4px)]">
                                <PackGroupInput
                                    disabled
                                    packGroups={packGroups}
                                    selectedPackGroupID={formData.packGroupID}
                                    setSelectedPackGroupID={(packGroup) =>
                                        updateConfig({ packGroupID: Number(packGroup) })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <Select
                        className="w-full"
                        value={formData.segment}
                        onValueChange={(value) => updateConfig({ segment: value })}
                    >
                        <SelectTrigger className="w-full">
                            <div className="flex items-center gap-4 ">
                                <span className="font-medium text-foreground">Segment</span>
                                <SelectValue placeholder="None" />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={null}>None</SelectItem>
                            <SelectSeparator />
                            {SEGMENT_VALUES.map((value) => (
                                <SelectItem key={value} value={value}>
                                    {value}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </CardContent>
        </Card>
    );
}

export default MetadataCard;
